"use client";
import { useCallback, useEffect, useState } from "react";

type Status = "idle" | "checking" | "available" | "registered" | "error";
type Result = { phone: string; status: Status; message?: string; testPasswordRegistration?: boolean; resumeRegistration?: boolean };

export function useRegistrationPhoneCheck(phone: string, enabled: boolean) {
  const [result, setResult] = useState<Result | null>(null);
  const [attempt, setAttempt] = useState(0);
  const valid = enabled && /^1[3-9]\d{9}$/u.test(phone);
  const status: Status = !valid ? "idle" : result?.phone === phone ? result.status : "checking";
  useEffect(() => {
    if (!valid) return;
    return scheduleRegistrationPhoneCheck(phone, setResult);
  }, [phone, valid, attempt]);
  const reset = useCallback(() => setResult(null), []);
  const retry = useCallback(() => { setResult(null); setAttempt(value => value + 1); }, []);
  const markRegistered = useCallback(() => setResult(previous => ({ phone, status: "registered", ...(previous?.phone === phone && previous.testPasswordRegistration === true ? { testPasswordRegistration: true } : {}) })), [phone]);
  return { status, resumeRegistration: valid && result?.phone === phone && result.resumeRegistration === true, testPasswordRegistration: valid && result?.phone === phone && result.testPasswordRegistration === true, message: result?.phone === phone ? result.message : undefined, retry, reset, markRegistered };
}

export function scheduleRegistrationPhoneCheck(phone: string, onResult: (result: Result) => void) {
  const controller = new AbortController();
  // The hook already requires a complete phone number; defer only to allow cleanup.
  const timer = setTimeout(async () => {
    try {
      const response = await fetch("/api/auth/registration-check", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ phone }),
        cache: "no-store", signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10_000)]),
      });
      if (response.status === 404) throw new Error("手机号检查服务尚未更新，请稍后重试");
      const body = await response.json();
      if (!response.ok || body.ok !== true || typeof body.registered !== "boolean") throw new Error(response.status === 429 ? "检查过于频繁，请稍后重试" : "暂时无法检查手机号，请重试");
      if (!controller.signal.aborted) onResult({ phone, status: body.registered ? "registered" : "available", ...(body.resumeRegistration === true && body.registered === false ? { resumeRegistration: true } : {}), ...(body.testPasswordRegistration === true ? { testPasswordRegistration: true } : {}) });
    } catch (error) {
      if (!controller.signal.aborted) onResult({ phone, status: "error", message: error instanceof Error && error.name === "Error" ? error.message : "暂时无法检查手机号，请重试" });
    }
  }, 0);
  return () => { clearTimeout(timer); controller.abort(); };
}
